
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConsolidatedNotifications } from "@/contexts/ConsolidatedNotificationContext";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { Bell, AlertCircle, Loader2, Wifi, WifiOff, Crown, Shield } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const EnhancedNotificationDropdown = () => {
  const { unreadCount, isLoading, error, retryConnection } = useConsolidatedNotifications();
  const { isSuperAdmin, hasAdminAccess, userRole } = useOptimizedAuth();
  const [isOpen, setIsOpen] = useState(false);

  const getConnectionIcon = () => {
    if (isLoading) return <Loader2 className="h-5 w-5 animate-spin" />;
    if (error) return <WifiOff className="h-5 w-5 text-red-500" />;
    return <Bell className="h-5 w-5" />;
  };

  const getConnectionStatus = () => {
    if (isLoading) return "Connecting...";
    if (error) return "Connection failed";
    return "Connected";
  };

  const getRoleIcon = () => {
    if (isSuperAdmin) return <Crown className="h-3 w-3" />;
    if (hasAdminAccess) return <Shield className="h-3 w-3" />;
    return null;
  };

  const getRoleText = () => {
    if (isSuperAdmin) return "Super Admin";
    if (userRole === 'admin') return "Admin";
    if (userRole === 'manager') return "Manager";
    return "User";
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          {getConnectionIcon()}
          {unreadCount > 0 && !isLoading && !error && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center rounded-full"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-96" align="end">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {!error ? (
                <Wifi className="h-4 w-4 text-green-500" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-500" />
              )}
              <span className="text-xs text-muted-foreground">
                {getConnectionStatus()}
              </span>
            </div>
          </div>

          {/* Role indicator */}
          {(isSuperAdmin || hasAdminAccess) && (
            <div className="flex items-center gap-2 mb-3">
              <Badge variant={isSuperAdmin ? "destructive" : "secondary"} className="text-xs">
                {getRoleIcon()}
                <span className="ml-1">{getRoleText()}</span>
              </Badge>
              <span className="text-xs text-muted-foreground">
                {isSuperAdmin ? "Viewing all notifications" : "Admin access enabled"}
              </span>
            </div>
          )}

          {unreadCount > 0 && !error && (
            <Badge variant="secondary" className="mb-4">
              {unreadCount} unread
            </Badge>
          )}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Connection Error</p>
                  <p className="text-sm">{error}</p>
                  {isSuperAdmin && (
                    <p className="text-xs mt-1 opacity-75">
                      Super admin privileges confirmed
                    </p>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={retryConnection}
                  className="ml-2"
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading notifications...</span>
            </div>
          )}

          {!isLoading && !error && unreadCount === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No new notifications</p>
              <p className="text-sm">
                {isSuperAdmin ? "All system notifications are up to date!" : "You're all caught up!"}
              </p>
            </div>
          )}

          {error && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <WifiOff className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Not connected</p>
              <Button variant="outline" size="sm" onClick={retryConnection} className="mt-2">
                Connect
              </Button>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EnhancedNotificationDropdown;
